import type { ComponentType, SVGProps } from 'react'
import {
  SparklesIcon,
  WrenchIcon,
  ClockIcon,
  LightBulbIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import type { NewsCategory, RouteInfo } from '../../api/news'

type Icon = ComponentType<SVGProps<SVGSVGElement>>

// One taxonomy for every surface: the same label, colour and icon in the app,
// the admin portal and here (server: src/services/news/blocks.js CATEGORIES).
export const CATEGORIES: Record<NewsCategory, { label: string; color: string; Icon: Icon }> = {
  release: { label: 'Release notes', color: '#f97316', Icon: SparklesIcon },
  fix: { label: 'Fixes', color: '#2563eb', Icon: WrenchIcon },
  maintenance: { label: 'Maintenance', color: '#d97706', Icon: ClockIcon },
  tip: { label: 'Tip', color: '#0d9488', Icon: LightBulbIcon },
  company: { label: 'Company news', color: '#7c3aed', Icon: MegaphoneIcon },
  security: { label: 'Security', color: '#dc2626', Icon: ShieldCheckIcon },
}
export const CATEGORY_KEYS = Object.keys(CATEGORIES) as NewsCategory[]

export function alpha(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`
}

/** RN app Tamagui tokens (light / dark). */
export interface SurfaceTokens {
  bg: string
  card: string
  border: string
  color: string
  primary: string
  secondary: string
}
export const APP_LIGHT: SurfaceTokens = { bg: '#f2ebe0', card: '#ffffff', border: '#d9cfc0', color: '#111827', primary: '#ff6b00', secondary: '#6b7280' }
export const APP_DARK: SurfaceTokens = { bg: '#1e1710', card: '#241c14', border: '#3a2e20', color: '#ffffff', primary: '#ff7719', secondary: '#9ca3af' }
/** Admin portal (gray / orange). */
export const PORTAL: SurfaceTokens = { bg: '#f3f4f6', card: '#ffffff', border: '#e5e7eb', color: '#111827', primary: '#ea580c', secondary: '#6b7280' }

// Fallback allow-lists; the composer loads the live ones from GET /super-admin/news/meta.
export const FALLBACK_APP_ROUTES: Record<string, RouteInfo> = {
  home: { label: 'Home', path: '/' },
  news: { label: 'News', path: '/news' },
  stock: { label: 'Stock tab', path: '/stock' },
  time_off: { label: 'Time off', path: '/time-off' },
  settings: { label: 'Settings', path: '/settings' },
}
export const FALLBACK_PORTAL_ROUTES: Record<string, RouteInfo> = {
  dashboard: { label: 'Dashboard', path: '/' },
  roles: { label: 'Roles & permissions', path: '/roles' },
  settings: { label: 'Settings', path: '/settings' },
}

export const ZONES: Array<{ value: string; label: string }> = [
  { value: 'company', label: "Company time (each company's own zone)" },
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { value: 'UTC', label: 'UTC' },
]

let keySeq = 0
/** Local React key for an editor block (never sent to the server). */
export const newKey = () => `b${Date.now().toString(36)}${(keySeq++).toString(36)}`
